import { Content } from 'antd/lib/layout/layout';
import Space from 'antd/lib/space';
import Title from 'antd/lib/typography/Title';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import styles from '../styles/appStyles';
import { LockOutlined } from '@ant-design/icons';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { tryVerifyAuthCode, TRY_VERIFY_AUTH_CODE, Verify2FACodeRequest } from '../../actions/login';
import { useDispatch, useSelector } from 'react-redux';
import { RedirectState } from '../../typescriptHelpers';
import routes from '../../routes';
import { RootState } from '../../reducers/reducers';
import qs from 'query-string';
import { updateTwoFactorAuthRequired } from '../../actions/auth';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const TwoFactorAuth: React.FC<RouteComponentProps> = (props: RouteComponentProps) => {
    const { location } = props;

    const [form] = Form.useForm();
    const history = useHistory();
    const dispatch = useDispatch();

    const twoFactorAuthRequired = useSelector((state: RootState) => state.auth.twoFactorAuthRequired);
    const userId = useSelector((state: RootState) => state.auth.userId);

    const redirectState: RedirectState = location.state;
    const redirectPath = redirectState?.fromLocation;

    const queryParams = qs.parse(props.location?.search);
    const requires2FAFromRedirect = queryParams.requires2FA;
    const requires2FAFromRedirectUserId = queryParams.userId as string;

    const verifyAuthCodeLoading = useIsLoading(TRY_VERIFY_AUTH_CODE);
    const verifyAuthCodeError = useError(TRY_VERIFY_AUTH_CODE);
    const verifyAuthCodeSuccess = useUpdated(TRY_VERIFY_AUTH_CODE);

    const submit = (values) => {
        const request: Verify2FACodeRequest = {
            code: values.code,
            userId: userId,
        };

        dispatch(tryVerifyAuthCode(request));
    };

    React.useEffect(() => {
        if (verifyAuthCodeError) {
            globalMessage.error(verifyAuthCodeError);
        }
    }, [verifyAuthCodeError]);

    React.useEffect(() => {
        if (verifyAuthCodeSuccess) {
            const redirectTo = redirectPath ?? routes.dashboard;

            history.replace(redirectTo);
        }
    }, [verifyAuthCodeSuccess]);

    React.useEffect(() => {
        if (requires2FAFromRedirect) {
            dispatch(updateTwoFactorAuthRequired(requires2FAFromRedirectUserId));

            // clear query param & prevent back navigation
            history.replace(window.location.pathname);
        } else if (!twoFactorAuthRequired) {
            // 2fa not required, redirect to login
            history.replace(routes.login);
        }
    }, []);

    return (
        <Content style={styles.centerVerticalAndHorizontal}>
            <Space direction="vertical" align="center" size="large">
                <Title style={{ fontSize: 24 }}>Verify your identity</Title>
                <Text type="secondary">Please enter the code shown in your authenticator app to continue.</Text>
                <Space direction="vertical" align="center" style={{ marginTop: 24 }}>
                    <Form form={form} onFinish={submit} style={{ minWidth: 320 }}>
                        <Form.Item rules={[{ required: true, message: 'Authentication code is required' }]} name="code">
                            <Input
                                placeholder="Authentication code"
                                prefix={<LockOutlined />}
                                style={{ minWidth: 300 }}
                                maxLength={6}
                                autoFocus
                            />
                        </Form.Item>
                    </Form>
                    <Button
                        type="primary"
                        htmlType="submit"
                        style={{ minWidth: 300 }}
                        loading={verifyAuthCodeLoading}
                        onClick={() => form.submit()}
                    >
                        Submit code
                    </Button>
                    <Button type="link" onClick={() => history.replace(routes.login)}>
                        Cancel
                    </Button>
                </Space>
            </Space>
        </Content>
    );
};

export default TwoFactorAuth;
